import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UserSummary } from "@pagemates/shared";
import { askForPush } from "./PushAsk";
import { api, describeError } from "../lib/api";
import { AskIcon, CloseIcon, LockIcon, PlusIcon, QuoteIcon, ReplyIcon, SendIcon } from "./Icons";
import { posLabel, type PosCtx } from "../lib/format";

type Mode = "open" | "here" | "custom" | "landmark";
type Kind = "message" | "quote" | "question";

export interface ComposerHandle { replyTo(name: string): void; }

/** The thread this composer sits under, when it is a question. */
export interface QuestionContext { asker: string; position: number; mine: boolean }

const listNames = (names: string[]) => names.length <= 2 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * The chat pill: a plus for the special kinds, the growing textarea, the
 * spoiler chip and send. Anything that needs explaining goes in a strip above
 * the pill, which only appears when there is something to say: a quote, a
 * question for people ahead, an answer to one, or a reply to someone.
 */
export interface ComposerExtra { key: string; label: string; about: string; icon: React.ReactNode; onPick: () => void }

export function Composer({ clubId, bookId, herePercent, hereLabel, parentId, placeholder, register, onPosted, ahead, question, myProgress, myDone, ctx, extras }:
  { clubId: string; bookId: string; herePercent: number; hereLabel: string; parentId?: string | null; placeholder: string;
    /** more + menu items that open their own sheet (leave a note forward, make a prediction) */
    extras?: ComposerExtra[];
    register?: (h: ComposerHandle) => void; onPosted?: () => void;
    /** stream only: who could answer a question asked here */
    ahead?: UserSummary[];
    /** thread only: set when the thread is a question */
    question?: QuestionContext; myProgress?: number; myDone?: boolean;
    /** the book's size, this reader's unit and the plan's landmarks, so positions read as "46% · p. 180" or "Part One · 52%" */
    ctx: PosCtx }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [mode, setMode] = useState<Mode>("here");
  const [custom, setCustom] = useState(herePercent);
  const [landmark, setLandmark] = useState<number | null>(null); // percent of a chosen landmark
  const [open, setOpen] = useState(false);
  const [plus, setPlus] = useState(false);
  const [kind, setKind] = useState<Kind>("message");
  const [replyName, setReplyName] = useState<string | null>(null);
  const [spoiler, setSpoiler] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const plusRef = useRef<HTMLDivElement>(null);

  const answering = Boolean(parentId && question && !question.mine);
  const spoilerAt = myDone ? 100 : Math.max(question?.position ?? 0, myProgress ?? 0);
  const locked = kind === "question" || Boolean(parentId && question);
  const position = kind === "question" ? herePercent
    : parentId && question ? (spoiler ? spoilerAt : question.position)
    : mode === "open" ? 0 : mode === "here" ? herePercent : mode === "landmark" ? (landmark ?? herePercent) : custom;

  const post = useMutation({
    mutationFn: () => api.addComment(clubId, bookId, {
      body: replyName ? `@${replyName} ${body}` : body, position, parentId: parentId ?? null,
      kind: parentId ? "message" : kind, ...(answering ? { spoiler } : {}),
    }),
    onSuccess: async () => {
      if (!parentId && kind === "question") askForPush("question"); // "Want to know when someone answers?"
      setBody(""); setKind("message"); setReplyName(null); setSpoiler(false);
      if (ta.current) ta.current.style.height = "auto";
      await qc.invalidateQueries({ queryKey: ["discussion", clubId, bookId] });
      onPosted?.();
    },
  });

  useEffect(() => {
    register?.({ replyTo(name) { setReplyName(name); ta.current?.focus(); } });
  }, [register]);
  useEffect(() => {
    if (!plus) return;
    const close = (e: MouseEvent) => { if (!plusRef.current?.contains(e.target as Node)) setPlus(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [plus]);

  function pick(k: Kind) { setKind(k); setPlus(false); ta.current?.focus(); }
  function grow(el: HTMLTextAreaElement) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 7.2 * 16)}px`; }
  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) {
      e.preventDefault();
      if (body.trim() && !post.isPending) post.mutate();
    }
  }
  const chipText = position === 0 && !locked ? "open" : mode === "landmark" && !locked ? posLabel(position, ctx) : `${position}%`;
  const aheadNames = (ahead ?? []).map((u) => u.name);

  return (
    <form className="composer" onSubmit={(e) => { e.preventDefault(); if (body.trim()) post.mutate(); }}>
      {open && !locked && (
        <div className="popover" role="radiogroup" aria-label="Who can see this">
          <button type="button" role="radio" aria-checked={mode === "open"} onClick={() => { setMode("open"); setOpen(false); }}>
            <b>Spoiler-free</b><span>Everyone sees it, whatever they've read.</span>
          </button>
          <button type="button" role="radio" aria-checked={mode === "here"} onClick={() => { setMode("here"); setOpen(false); }}>
            <b>{hereLabel} · {herePercent}%</b><span>Sealed for anyone who isn't there yet.</span>
          </button>
          {(ctx.landmarks?.length ?? 0) > 0 && <div className="grp">Landmarks</div>}
          {ctx.landmarks?.map((l) => (
            <button key={l.name + l.percent} type="button" role="radio" className="lm" aria-checked={mode === "landmark" && landmark === l.percent} onClick={() => { setMode("landmark"); setLandmark(l.percent); setOpen(false); }}>
              <b>Through {l.name} <small>{l.percent}%{l.page && (ctx.myFormat ?? "pages") === "pages" ? ` · p. ${l.page}` : ""}</small></b>
            </button>
          ))}
          <button type="button" role="radio" aria-checked={mode === "custom"} className="custom"
            onClick={(e) => { setMode("custom"); (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.focus(); }}>
            <span><b>Somewhere else</b><span>Pick the percent it's about.</span></span>
            <input className="input num" type="number" min={0} max={100} value={custom} aria-label="Percent"
              onClick={(e) => e.stopPropagation()} onFocus={() => setMode("custom")}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setOpen(false); } }}
              onChange={(e) => setCustom(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} />
          </button>
        </div>
      )}

      {kind === "quote" && (
        <div className="mode-strip">
          <QuoteIcon /><div className="t"><b>Quote from the book</b><small>In the book's voice, at your spot.</small></div>
          <button type="button" className="x" aria-label="Back to a plain message" onClick={() => setKind("message")}><CloseIcon /></button>
        </div>
      )}
      {kind === "question" && (
        <div className="mode-strip">
          <AskIcon /><div className="t"><b>Question for people ahead</b>
            <small>{aheadNames.length ? `${listNames(aheadNames)} ${aheadNames.length === 1 ? "is" : "are"} past ${posLabel(herePercent, ctx)} and will answer without spoilers.` : `Nobody is past ${posLabel(herePercent, ctx)} yet. Your question will wait for them.`} Say where you are, like "just after chapter 4".</small></div>
          <button type="button" className="x" aria-label="Back to a plain message" onClick={() => setKind("message")}><CloseIcon /></button>
        </div>
      )}
      {answering && question && (
        <div className={`mode-strip${spoiler ? " spoiler" : ""}`}>
          <LockIcon />
          {spoiler ? (
            <div className="t"><b>Spoiler answer</b><small>Sealed for {question.asker} until {posLabel(spoilerAt, ctx)}. <button type="button" className="link" onClick={() => setSpoiler(false)}>Keep it spoiler-free</button></small></div>
          ) : (
            <div className="t"><b>Answering {question.asker}, who's at {posLabel(question.position, ctx)}.</b><small>Not sure what they've reached? <button type="button" className="link" onClick={() => setSpoiler(true)}>Mark it as a spoiler</button> and it opens when they get there.</small></div>
          )}
        </div>
      )}
      {replyName && (
        <div className="mode-strip reply">
          <ReplyIcon /><div className="t">Replying to <b>{replyName}</b></div>
          <button type="button" className="x" aria-label="Stop replying" onClick={() => setReplyName(null)}><CloseIcon /></button>
        </div>
      )}

      <div className="pill-row">
        {!parentId && (
          <div className="plus-wrap" ref={plusRef}>
            <button type="button" className={`plus${plus ? " on" : ""}`} aria-label="More ways to post" aria-expanded={plus} onClick={() => setPlus((p) => !p)}><PlusIcon /></button>
            {plus && (
              <div className="popover plus-menu" role="menu">
                <button type="button" role="menuitem" className="opt" onClick={() => pick("quote")}>
                  <span className="bub"><QuoteIcon /></span><span><b>Share a quote</b><span>A passage from the book, shown with your spot.</span></span>
                </button>
                <button type="button" role="menuitem" className="opt" onClick={() => pick("question")}>
                  <span className="bub"><AskIcon /></span><span><b>Ask people ahead</b><span>A question for readers past you. Answers stay spoiler-free.</span></span>
                </button>
                {extras?.map((x) => (
                  <button key={x.key} type="button" role="menuitem" className="opt" onClick={() => { setPlus(false); x.onPick(); }}>
                    <span className="bub">{x.icon}</span><span><b>{x.label}</b><span>{x.about}</span></span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className={`pill-box${kind === "quote" ? " quoting" : ""}`}>
          <textarea ref={ta} id={`compose-${parentId ?? "top"}`} rows={1} value={body}
            placeholder={kind === "quote" ? "Paste the passage" : kind === "question" ? "Your question" : answering ? "Answer, no spoilers" : placeholder}
            maxLength={kind === "quote" ? 1000 : 4000}
            onChange={(e) => { setBody(e.target.value); grow(e.target); }} onKeyDown={onKey} />
          <div className="pill-tail">
            {locked ? (
              <span className="chip locked" aria-label={`Posted at ${position}%`}><LockIcon width={11} height={11} />{position}%</span>
            ) : (
              <button type="button" className={`chip${mode === "open" ? " open" : ""}`} aria-label={`Visibility: ${chipText}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>{chipText}</button>
            )}
            <button type="submit" className="send" disabled={!body.trim() || post.isPending} aria-label={parentId ? "Reply" : "Send"}><SendIcon /></button>
          </div>
        </div>
      </div>
      {post.isError && <p className="error hint-line">{describeError(post.error)}</p>}
    </form>
  );
}
